/** A step's named exits in document mode (E3, spec 2026-09-18 §6 Edit):
 * name, one target picked over steps / close outcomes / notify seats (an
 * exit names exactly one — `ExitSpec`), and the fields it `requires`. */
import { useEffect, useState } from 'react'
import { Button, Input, NativeSelect, TagInput } from '@/components/ui'
import type { ProcessExitJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { normalizeExit } from '@/utils/workflowOverview'
import { KEY_PATTERN } from './stepSkeleton'

const targetValue = (e: ProcessExitJson) =>
  e.to != null ? `to:${e.to}` : e.close != null ? `close:${e.close}` : `notify:${e.notify ?? ''}`

/** Only the chosen target key is written — the published bytes never carry
 * the two unused ones. `requires` stays when the exit already had the key
 * (the published dump writes it empty), so a retarget-and-back is no edit. */
function targetExit(value: string, requires: string[], keepEmpty: boolean): ProcessExitJson {
  const [kind, ...rest] = value.split(':')
  return { [kind]: rest.join(':'), ...(requires.length > 0 || keepEmpty ? { requires } : {}) } as ProcessExitJson
}

/** A rename moves a key, so it commits on blur or Enter, never per keystroke.
 * Controlled, and the rows are keyed by position: the row must not remount
 * under the keyboard when its own name changes. */
function ExitNameInput({ name, taken, onRename }: { name: string; taken: string[]; onRename: (to: string) => void }) {
  const [text, setText] = useState(name)
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => {
    setText(name)
    setProblem(null)
  }, [name])
  function commit() {
    const next = text.trim()
    if (next === name) return setProblem(null)
    if (!KEY_PATTERN.test(next)) return setProblem('Lowercase letters, digits and underscores, starting with a letter.')
    if (taken.includes(next)) return setProblem(`“${next}” is already an exit of this step.`)
    onRename(next)
  }
  return (
    <div className="space-y-1">
      <Input
        aria-label={`Name of exit ${name}`}
        aria-invalid={problem !== null}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit()
        }}
      />
      {problem && <p className="text-destructive">{problem}</p>}
    </div>
  )
}

export function ProcessExitEditor({
  stepId,
  doc,
  onSet,
  onRename,
  newExit,
}: {
  stepId: string
  doc: ProcessWorkflowDefinitionJson
  /** `null` removes the exit. */
  onSet: (name: string, exit: ProcessExitJson | null) => void
  onRename: (from: string, to: string) => void
  /** The exit an added row starts as (the caller knows the reading order). */
  newExit: ProcessExitJson
}) {
  const [adding, setAdding] = useState('')
  const exits = doc.steps[stepId]?.exits ?? {}
  const names = Object.keys(exits)
  const stepIds = Object.keys(doc.steps)
  const numberOf = (id: string) => stepIds.indexOf(id) + 1
  const addable = KEY_PATTERN.test(adding) && !names.includes(adding)
  const label = (what: string, name: string) => `${what} of exit ${name}`

  return (
    <fieldset className="space-y-2 rounded-xl border border-border/60 p-3">
      <legend className="px-1 text-xs font-semibold text-foreground">Next</legend>
      {names.map((name, index) => {
        // A pasted document may carry the short form (`done: next_step`).
        const exit = normalizeExit(exits[name])
        const value = targetValue(exit)
        const keepEmpty = exit.requires !== undefined
        return (
          <div key={index} className="grid gap-2 text-xs sm:grid-cols-[10rem_1fr_1fr_auto] sm:items-start">
            <ExitNameInput name={name} taken={names} onRename={(to) => onRename(name, to)} />
            <NativeSelect
              aria-label={label('Target', name)}
              value={value}
              onChange={(e) => onSet(name, targetExit(e.target.value, exit.requires ?? [], keepEmpty))}
            >
              {/* A target the document no longer declares still shows, so the
               * select never silently rewrites it. */}
              {!isKnownTarget(value, doc) && <option value={value}>{value} (unknown)</option>}
              <optgroup label="Go to step">
                {stepIds.map((id) => (
                  <option key={id} value={`to:${id}`}>
                    {numberOf(id)} {doc.steps[id].title?.en ?? id}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Close the record as">
                {(doc.record?.outcomes ?? []).map((o) => (
                  <option key={o} value={`close:${o}`}>
                    {o.replace(/_/g, ' ')}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Stay here and tell">
                {Object.entries(doc.seats ?? {}).map(([id, seat]) => (
                  <option key={id} value={`notify:${id}`}>
                    {seat.label?.en ?? id}
                  </option>
                ))}
              </optgroup>
            </NativeSelect>
            <TagInput
              ariaLabel={label('Required fields', name)}
              value={exit.requires ?? []}
              onChange={(requires) => onSet(name, targetExit(value, requires, keepEmpty))}
              suggestions={Object.keys(doc.fields ?? {})}
              normalize={(raw) => raw.trim()}
              maxTags={200}
              placeholder="Requires…"
            />
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remove exit ${name}`}
              disabled={names.length === 1}
              title={names.length === 1 ? 'A step keeps at least one exit' : undefined}
              onClick={() => onSet(name, null)}
            >
              Remove
            </Button>
          </div>
        )
      })}
      <div className="flex items-center gap-2">
        <Input
          aria-label="New exit name"
          className="max-w-40"
          placeholder="new_exit_name"
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && addable) {
              onSet(adding, newExit)
              setAdding('')
            }
          }}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!addable}
          onClick={() => {
            onSet(adding, newExit)
            setAdding('')
          }}
        >
          Add exit
        </Button>
      </div>
    </fieldset>
  )
}

function isKnownTarget(value: string, doc: ProcessWorkflowDefinitionJson): boolean {
  const [kind, ...rest] = value.split(':')
  const id = rest.join(':')
  if (kind === 'to') return id in doc.steps
  if (kind === 'close') return (doc.record?.outcomes ?? []).includes(id)
  return id in (doc.seats ?? {})
}
